import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { copyAmountChange } from '../../store/copiesList/actions';
import { createSelectionlist, setActiveList } from '../../store/orderlist/actions';
import DocumentTitle from '../../shared/components/DocumentTitle';
import { changeGroupBy, collapseGroups } from '../../store/grouping/actions';
import * as types from '../../store/constants';
import { GroupBy, ListContentAction } from '../../store/constants';
import {
    deleteCart,
    downloadCSV,
    failAddToCart,
    removeFromCart,
    removeProductFromCart
} from '../../store/products/actionsCart';
import { AppState } from '../../store/appState';
import { AppDispatch } from '../../store';
import { ProductMetaData, ProductSorting, UsageCondition } from '../../../models/Product';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { Orderlist } from '../../../models/OrderList';
import { getGroupItemId, GroupItem } from '../../../models/GroupItem';
import { PriceTier } from '../../../models/PriceTier';
import { BulkActionResponse } from '../../store/api';
import { useHistory, useLocation } from 'react-router';
import queryString from 'query-string';
import { invalidateCartGroupedItems, useCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { modifyURLState, modifyURLStates } from '../../shared/utils/utils-routing';
import Base64 from '../../shared/utils/base64';
import {
    invalidateShoppingCartProducts,
    useShoppingCartProducts
} from '../../api/shopping-cart/useShoppingCartProducts';
import { displayCart } from '../../store/header/actions';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { CartHeader } from './CartHeader';
import {
    deselectAll,
    deselectOne,
    isInSelectionMode,
    isSelected,
    selectAll,
    selectOne,
    setSelectionMode
} from '../../store/productSelection/actions';
import FilterBar from '../../shared/common/FilterBar';
import { FilterAndProductListWrapper } from '../../shared/common/FilterAndProductListWrapper';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import ProductList from '../../shared/common/ProductList';
import CartProductTile from './CartProductTile';
import CartListItem from './CartListItem';
import Pagination, { getPaginationStateObject } from '../../shared/common/Pagination';
import GroupedByList from '../../shared/common/GroupedByList';
import { CartSelectionlistItem } from './CartSelectionlistItem';
import GroupedByItem from '../../shared/bundles/GroupedByItem';
import { DisplayTypeState } from '../../store/displayType/state';
import { useBulkChangeSubscriptionPeriod } from './useBulkChangeSubscriptionPeriod';
import { deleteCartGroupedItem } from './api/deleteCartGroupedItem';
import { invalidateCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { changeAmountGroupedItem } from './api/changeAmountGroupedItem';
import { AuthenticationState } from '../../store/authentication/state';
import { fetchCartAggregationsQuickSearch } from '../../api/shopping-cart/fetchCartAggregationsQuicksearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { invalidateCartAggregations, useCartAggregations } from './useCartAggregations';
import { disableListLoading, enableListLoading } from '../../store/listLoading/actions';
import { useBulkUpdateUsageConditions } from '../../api/shopping-cart/bulkUpdateUsageConditions';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useCopyBulkToSelectionList } from '../../api/selection-list/useCopyToList';
import { ListSourceType } from '../../../models/ListSourceType';
import { convertSelectionState } from '../../shared/product-selection/ProductSelectionTypes';
import { useMoveBulkToSelectionList } from '../../api/selection-list/useMoveToList';
import { useChangePriceInCart } from '../../api/shopping-cart/useChangePriceInCart';
import { useCopyBundleGroupToSelectionList } from '../../api/selection-list/useCopyBundleGroupToList';
import { useMoveBundleGroupToSelectionList } from '../../api/selection-list/useMoveBundleGroupToList';
import { useMoveSingleToSelectionList } from '../../api/selection-list/useMoveSingleToList';
import { useCopySingleToSelectionList } from '../../api/selection-list/useCopySingleToList';
import {
    isSingleCopyOptions,
    isSingleMoveOptions,
    SingleCopyOrMoveOptions
} from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
export const DEFAULT_PAGE_SIZE = 54;

export const CartPage = () => {
    const { t } = useTranslation();
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch<AppDispatch>();
    const authentication = useSelector((state: AppState) => state.authentication);

    const { groupBy: groupByQueryParam } = queryString.parse(search);
    const isListLoading = useSelector((state: AppState) => state.listLoading.loading);

    const parsedGrouping: { key: GroupBy; id: string | null } = useMemo(
        () => (!groupByQueryParam ? { key: types.GROUPBY_PRODUCTS } : JSON.parse(Base64.decode(groupByQueryParam))),
        [groupByQueryParam]
    );

    useLayoutEffect(() => {
        dispatch(displayCart());
        dispatch(disableListLoading());
    }, [dispatch]);

    const isGroupedBy = parsedGrouping.key !== types.GROUPBY_PRODUCTS;

    useEffect(() => {
        if (isGroupedBy) {
            dispatch(changeGroupBy(parsedGrouping));
        }
    }, [dispatch, isGroupedBy, parsedGrouping]);

    const isSticky = useSelector((state: AppState) => state.header.isSticky);
    const productSelection = useSelector((state: AppState) => state.productSelection);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        ageCategory: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.TITLE_AZ;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { products, isLoading: isProductsLoading } = useShoppingCartProducts({
        pagination: {
            page,
            pageSize,
            sortBy
        },
        filterMap: filter,
        groupType: parsedGrouping.key,
        groupId: parsedGrouping.id
    });

    const { data: groupedItems, isInitialLoading: isGroupedItemsLoading } = useCartGroupedItems({
        groupKey: parsedGrouping.key ?? types.GROUPBY_PRODUCTS,
        enabled: isGroupedBy
    });

    const { mutate: onBulkChangeSubscriptionPeriod, isLoading: isLoadingBulkChangeSubscriptionPeriod } =
        useBulkChangeSubscriptionPeriod(filterQueryParam, convertSelectionState(productSelection, products.count));

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onCreateNewListClicked(newSelectionName: string) {
        dispatch(createSelectionlist(newSelectionName));
    }

    function onCopyAmountChange(productIdOrOptions: any, amount: number, command: string = 'set') {
        dispatch(copyAmountChange(productIdOrOptions, amount, command, false));
    }

    function onRemoveFromCart() {
        dispatch(removeFromCart());
    }

    function onRemoveProductFromCart(product: ProductMetaData & Article) {
        dispatch(removeProductFromCart(product));
    }

    const { copyToList } = useCopySingleToSelectionList({ source: ListSourceType.CART });
    const { moveToList } = useMoveSingleToSelectionList({ source: ListSourceType.CART });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleCopyOrMoveOptions) => {
            if (isSingleCopyOptions(options) && options.target === ListActionTarget.List && copyToList) {
                return copyToList(options.options, options.listId, performChecks);
            } else if (isSingleMoveOptions(options) && options.target === ListActionTarget.List && moveToList) {
                return moveToList(options.options, options.listId, performChecks);
            } else {
                throw new Error('illegal cart action');
            }
        },
        [copyToList, moveToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<SingleCopyOrMoveOptions>({ onAction });

    const bulkUpdateUsageConditions = useBulkUpdateUsageConditions();

    async function onUpdateUsageCondition(
        product: ProductMetaData & Article,
        usageCondition: UsageCondition,
        subscriptionPeriodType: SubscriptionPeriodType | undefined = undefined
    ) {
        const sourceUsageCondition = product.price
            ? {
                  lendingModel: product.price.lendingModel!,
                  usageLimit: product.price.usageLimit!,
                  termLimit: product.price.termLimit!
              }
            : {};
        const sourceSubscriptionPeriodType = product.subscriptionPeriod?.type;
        const targetSubscriptionPeriodType = subscriptionPeriodType ?? product.subscriptionPeriod?.type;

        await bulkUpdateUsageConditions([
            {
                productId: product.id,
                sourceUsageCondition: sourceUsageCondition,
                targetUsageCondition: usageCondition,
                sourceSubscriptionPeriodType,
                targetSubscriptionPeriodType
            }
        ]);
    }

    function onRemoveGroupedItemFromCart(item: GroupItem) {
        const group = { ...parsedGrouping, id: getGroupItemId(item) };
        dispatch(enableListLoading());
        deleteCartGroupedItem(group)
            .then(() => {
                invalidateCartSummaryQuery().then();
                dispatch(collapseGroups());
                invalidateShoppingCartProducts().then();
                invalidateCartGroupedItems().then();
            })
            .catch(error => {
                dispatch(failAddToCart(error, types.SOURCE_CART));
            })
            .finally(() => {
                dispatch(disableListLoading());
            });
    }

    const { copyToListBulk: copyBundleGroupToList } = useCopyBundleGroupToSelectionList({
        source: ListSourceType.CART
    });

    function onCopyBundleGroupToList(item: GroupItem, list: Orderlist, performChecks: boolean) {
        return copyBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }

    const { isLoading: isLoadingMoveGroupToList, moveToListBulk: moveBundleGroupToList } =
        useMoveBundleGroupToSelectionList({
            source: ListSourceType.CART
        });

    function onMoveBundleGroupToList(item: GroupItem, list: Orderlist, performChecks: boolean) {
        return moveBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }

    function onGroupAmountChange(item: GroupItem, amount: number, command: ListContentAction) {
        const group = {
            ...parsedGrouping,
            id: getGroupItemId(item)
        };
        dispatch(enableListLoading());
        changeAmountGroupedItem(group, amount, command)
            .then(() => {
                invalidateCartSummaryQuery().then();
                dispatch(collapseGroups());
                invalidateShoppingCartProducts().then();
                invalidateCartGroupedItems().then();
            })
            .catch(error => {
                dispatch(failAddToCart(error, types.SOURCE_CART));
            })
            .finally(() => {
                dispatch(disableListLoading());
            });
    }

    function onDownloadCSV() {
        dispatch(downloadCSV());
    }

    const onGroupBy = useCallback(
        (group: { readonly key: string }) => {
            dispatch(changeGroupBy(group));
        },
        [dispatch]
    );

    const { changePriceBulk } = useChangePriceInCart({
        filter: filterQueryParam ?? ''
    });

    function onChangePrice(priceTier: PriceTier, performChecks: boolean = false): Promise<BulkActionResponse> {
        return changePriceBulk(priceTier, performChecks, convertSelectionState(productSelection, products.count));
    }

    function doDeleteCart() {
        dispatch(deleteCart());
    }

    const { copyToListBulk: copyBulkProductsToList } = useCopyBulkToSelectionList({
        filter: filterQueryParam ?? '',
        source: ListSourceType.CART
    });

    const onBulkCopyToList = (list: Orderlist, performChecks: boolean) => {
        return copyBulkProductsToList(
            list.id,
            performChecks,
            convertSelectionState(productSelection, products.count)
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(list));
            }
            return data;
        });
    };

    const { isLoading: isLoadingMoveToList, moveToListBulk: moveBulkProductsToList } = useMoveBulkToSelectionList({
        filter: filterQueryParam ?? '',
        source: ListSourceType.CART
    });

    const onBulkMoveToList = (list: Orderlist, performChecks: boolean) => {
        return moveBulkProductsToList(
            list.id,
            performChecks,
            convertSelectionState(productSelection, products.count)
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(list));
            }
            return data;
        });
    };

    function handleProductSelection(productOrId: string | (ProductMetaData & Article)) {
        const selected = isSelected(productOrId, productSelection);
        if (!selected) {
            dispatch(selectOne(productOrId));
        } else {
            dispatch(deselectOne(productOrId));
        }
    }

    function onSelectAll() {
        dispatch(selectAll(products.count));
    }

    function onDeselectAll() {
        dispatch(deselectAll());
    }

    function cancelSelectionMode() {
        dispatch(setSelectionMode(false));
    }

    function toggleSelectionMode() {
        dispatch(setSelectionMode(!isInSelectionMode(productSelection)));
    }

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => cartPageFilterGroups(authentication), [authentication]);
    const { aggregationsQueryKeyBuilder, fetchAggregations } = useCartAggregations(parsedGrouping);
    const fetchCartAggregationsQuickSearchCallback = useCallback(fetchCartAggregationsQuickSearch, []);
    //endregion

    const isNotGroupExpanded = isGroupedBy && !parsedGrouping.id;

    const itemComponent = parsedGrouping.key === types.GROUPBY_LISTS ? CartSelectionlistItem : GroupedByItem;

    const loading =
        isProductsLoading ||
        isGroupedItemsLoading ||
        isLoadingBulkChangeSubscriptionPeriod ||
        isListLoading ||
        isLoadingMoveToList ||
        isLoadingMoveGroupToList;

    return (
        <DocumentTitle title={t('components.documentTitle.cart')}>
            <ContainerInnerWithColumns>
                {loading ? (
                    <LoadingWrapper>
                        <LoadingOverlay loading={loading} />
                    </LoadingWrapper>
                ) : null}
                <>
                    <CartHeader
                        isSticky={isSticky}
                        isInSelectionMode={isInSelectionMode(productSelection)}
                        deselectAll={onDeselectAll}
                        cancelSelectionMode={cancelSelectionMode}
                        onMoveCartToList={onBulkMoveToList}
                        onMoveToList={onBulkMoveToList}
                        onCopyCartToList={onBulkCopyToList}
                        onCopyToList={onBulkCopyToList}
                        onDelete={doDeleteCart}
                        onCreateNewListClicked={onCreateNewListClicked}
                        onChangePrice={onChangePrice}
                        onDownloadCSV={onDownloadCSV}
                        onCopyAmountChange={onCopyAmountChange}
                        onRemoveFromCart={onRemoveFromCart}
                        onBulkChangeSubscriptionPeriod={onBulkChangeSubscriptionPeriod}
                        totalProducts={products.count}
                    />
                    <FilterBar
                        productSelection={productSelection}
                        dispatch={dispatch}
                        onSortChange={setSortBy}
                        displayType={display as DisplayTypeState}
                        totalProducts={products.count}
                        loading={loading}
                        onGroupBy={onGroupBy}
                        sortBy={sortBy}
                        onChangePrice={onChangePrice}
                        onPageChange={setPage}
                        currentPage={page}
                        pageSize={pageSize}
                        grouping={parsedGrouping}
                        toggleSelectionMode={toggleSelectionMode}
                        selectAll={onSelectAll}
                        onFilterDisplayToggle={onFilterDisplayToggle}
                        onDisplayChange={onDisplayChange}
                        groupByGroups={[
                            {
                                key: types.GROUPBY_LISTS,
                                label: 'Selection Lists'
                            }
                        ]}
                        hidePagination={isGroupedBy}
                        hideSelectButtons={isNotGroupExpanded}
                        hideSelectAll={isGroupedBy}
                        displayFilter={displayFilter}
                    />
                </>
                <FilterAndProductListWrapper>
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchCartAggregationsQuickSearchCallback}
                        />
                    ) : null}
                    <div className={classnames('ca-main', { 'min-h-screen': loading })}>
                        {parsedGrouping.key === types.GROUPBY_PRODUCTS ? (
                            !loading && products.count === 0 ? (
                                <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                            ) : (
                                <div className="ca-main-inner">
                                    <ProductList
                                        products={products.list}
                                        displayType={display}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                        sortBy={sortBy}
                                        grouping={parsedGrouping}
                                        productSelection={productSelection}
                                        onSelectProduct={handleProductSelection}
                                        tileItemComponent={CartProductTile}
                                        listItemComponent={CartListItem}
                                        onCreateNewListClicked={onCreateNewListClicked}
                                        onCopyAmountChange={onCopyAmountChange}
                                        onAddPrice={changePrice}
                                        onUpdateUsageCondition={onUpdateUsageCondition}
                                        onRemoveProductFromCart={onRemoveProductFromCart}
                                    />
                                    <Pagination
                                        totalItems={products.count}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )
                        ) : groupedItems && groupedItems?.length > 0 ? (
                            <div className="ca-main-inner groupby-cart" key={`${parsedGrouping.key}_group`}>
                                <GroupedByList
                                    totalProducts={products.count}
                                    currentPage={page}
                                    pageSize={pageSize}
                                    products={products.list}
                                    displayType={display as unknown as DisplayTypeState}
                                    onPageChange={setPage}
                                    productSelection={productSelection}
                                    onCopyGroupToList={onCopyBundleGroupToList}
                                    onMoveGroupToList={onMoveBundleGroupToList}
                                    onCopyAmountChange={onCopyAmountChange}
                                    tileItemComponent={CartProductTile}
                                    listItemComponent={CartListItem}
                                    dispatch={dispatch}
                                    grouping={parsedGrouping}
                                    onGroupAmountChange={onGroupAmountChange}
                                    // FIXME the state typings should allow this without an explicit cast
                                    groups={groupedItems as unknown as ReadonlyArray<GroupItem>}
                                    onSelectProduct={handleProductSelection}
                                    onRemoveGroupedItemFromList={onRemoveGroupedItemFromCart}
                                    itemComponent={itemComponent}
                                    deselectAll={onDeselectAll}
                                    onGroupBy={onGroupBy}
                                    onCreateNewListClicked={onCreateNewListClicked}
                                    onAddPrice={changePrice}
                                    onRemoveProductFromCart={onRemoveProductFromCart}
                                    onUpdateUsageCondition={onUpdateUsageCondition}
                                    invalidateAggregations={invalidateCartAggregations}
                                />
                            </div>
                        ) : null}
                    </div>
                </FilterAndProductListWrapper>
            </ContainerInnerWithColumns>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
        </DocumentTitle>
    );
};

const cartPageFilterGroups = (authentication: AuthenticationState) => [
    ...defaultVisibleFilterGroups(authentication),
    'selectedLendingModel',
    'selectedUsageCondition',
    'selectedPrice'
];
